//initial redux
import moment from "moment-timezone";
import { all, put, call, fork, select, take, takeEvery } from "redux-saga/effects";
import pointsservice from "../../../../services/points";
import reportsservice from "../../../../services/reports";
import rulessservice from "../../../../services/rules";
import { types as sitetreetypes } from '../../../../services/SiteTree/redux';
import { DateTime, number } from "../../../../utils";
import pointName from "../../../../utils/pointName";
import seriesColor from "../../../../utils/seriesColor";
import steppedSeries from "../../../../utils/steppedSeries";
import unitsservice from "../../../../utils/units";
import { actions as charttooltipactions } from "../ChartTooltip/redux";
import { actions as seriesColorActions } from "../../../../services/SeriesColor/redux";

const initialState = {
	genericComments: []
};


/////////
//types
export const types = {
	LOAD_DATA: "components/validationrulechart/LOAD_DATA",
	SET_GENERIC_COMMENTS: "components/validationrulechart/SET_GENERIC_COMMENTS"
};

///////////
//reducers
export const reducers = {
	reducer(state = initialState, action) {
		switch (action.type) {
			case types.LOAD_DATA:{
				return state;
			}
			case types.SET_GENERIC_COMMENTS: {
				return {...state, genericComments: action.payload.comments}
			}
			default:
				return state;
		}
	}
};

function getColorForRunState(runState){
	switch(runState){
		case 0: //pre con fail
            return null;
		case 1: //test start
            return null;
		case 2: //rule pass
			return "#C7F6CC";
		case 3: //rule fail
			return "#FCD1D0";
		case 4: //rule pause
			return "#FDEABF";
		case 5: //test end
            return null;
        case 6: //rule abort
            return null;
		default:
            return null;
	}
}

//TODO => toDate needs to be replaced with the last processed date on the rule engine for the rule instance.
function addOutcomes(chart, outcomePeriods, toDate){
	if(!chart.options){return;}
	if(outcomePeriods && outcomePeriods.length){
        outcomePeriods.forEach(outcomePeriod => {
            var bandColor = getColorForRunState(outcomePeriod.runState);
            if (bandColor != null) {
					if(bandColor && outcomePeriod && outcomePeriod.startSiteLocal && (outcomePeriod.endSiteLocal || toDate)) {
						chart.xAxis[0].addPlotBand({
							color: bandColor,
							from: new Date(outcomePeriod.startSiteLocal).getTime(),
							to: outcomePeriod.endSiteLocal != null ? new Date(outcomePeriod.endSiteLocal).getTime() : toDate
						});
					}
            }
		});
		const fails = outcomePeriods.filter(x => x.runState === 3);
		if(fails.length){
			const pinseries = {
				data: fails.map(x => [new Date(x.startSiteLocal).getTime(), 1]),
				name: "Fail Start Time",
				yAxis:  getAxisIndex(chart, "failstart", 0, 1, false, true),
				lineWidth: 0,
				color: '#f00',
				marker: {
					enabled: true,
					symbol: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAkAAAAOCAYAAAD9lDaoAAAACXBIWXMAAAsSAAALEgHS3X78AAAAmUlEQVQoFbWQsQ3CMBBFX7wArtwCHSV9HIsRGCGjJRswgmV5AEpKKJOOCYwcxYAxkWh40knnf6+4cxVCIDEafUi9ct6mfpJGo/fACVjz4gYclfPnamhqCVyBFSV3YCOAdkFgztsoyWKUI0URfSFKtowzrJhP7ReEPs6f/zQaHQ+ItQMuQKec76ZhlN5raOrwmf28+P+kbfYCHhrAP2DoSV86AAAAAElFTkSuQmCC)'
				},
				zIndex: 90,
			};

			chart.addSeries(pinseries)
		}
	}
}

function* addComments(chart, comments, id) {
	if(!chart.options) { return; }
	
	let newSeries = {
		name: 'Comments',
		color: '#526BAF',
		yAxis: getAxisIndex(chart, 'C^', 0, 100, false, true),
		data: [],
		marker: {
				enabled: true,
				symbol: 'circle'
		},
		lineWidth: 0,
		states: {
			hover: {
				lineWidth: 0,
				lineWidthPlus: 0,
			}
		},
	};
	let genericComments = [];

	if(comments && comments.length) {
		comments.forEach(s => {
			if(!s.toDate && s.y !== null && !s.isGeneric) {
				newSeries.data.push({x: DateTime.getGMTDate(s.fromDate.toString()).getTime(), y: s.y, comment: s.description });
			} else {
				genericComments.push(s);
			}
		});
	}

	if(chart && chart.options){
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}

	return genericComments;
}

//adds a plot line (i.e static value line) to chart 
function addPlotLine(chart, value, units, fromDate, toDate){
	if(!chart.options){return;}
	let axisIndex = getAxisIndex(chart, units);
	chart.yAxis[axisIndex].addPlotLine({
		value: value,
		width: 2,
		color: 'red',
		zIndex: 10,
	});
	//the chart wont show the line if the series for the same axis doesnt have values near it (i.e the axis wont auto scale like with series)
	//to combat this, add a new, invisible series for the same axis with a single point with the same value as the plotline
	//get the midpoint of the x axis
	let xAxisMidpoint = (fromDate.toDate().getTime() + toDate.toDate().getTime()) / 2;
	chart.addSeries({
		yAxis: axisIndex,
		data: [{x: xAxisMidpoint, y: Number(value)}],
		marker: {
			enabled: false,
		},
		enableMouseTracking: false,
		showInLegend: false,
	});
}

function configYAxis(units, min, max, visible) {
	let newYAxis = {
		min, 
		max,
		visible,
		title: {
			text: units,
			align: "high",
			rotation: 0,
			margin: 0,
			y: -30,
			offset: 14,
			style: {
				"font-weight": "600",
				"font-size": "15px",
			},
		},  
		labels: {
			style: {
				fontSize: "14px",
				color: '#999',
			},
			formatter() {
				return number.parseNumber(this.value, 1).replace(/0*$|\.0*$|\.$/g, "");
			},
		}
	}

	if (units == 'inSession' ) {
		newYAxis.min = 0;
		newYAxis.max = 1;
		newYAxis.height = '20%';
		newYAxis.top = '80%';
	}

	return newYAxis;
}

//returns teh index of the y index with teh specified units.
//if it doesn't exist it creates a new axis with teh units and returns the index for that axis.
function getAxisIndex(chart, units, min, max, visible = true, forceNew = false){
	if(!chart.options){return;}
	let axisIndex = 0;
	let symbol = unitsservice.stringToSymbol(units);
	units = symbol ? symbol : units;
	//if the first yaxis doesnt have a value set then
	if(!forceNew && !chart.yAxis[0].userOptions.title){
		chart.yAxis[0].update(configYAxis(units, min, max, visible));
	}else{
		//search for existing with same units and use that, else add a new axis.
		let axisFound = false;
		for(var ii = 0; ii < chart.yAxis.length; ii++){
			let wkAxis = chart.yAxis[ii];
			if(wkAxis.userOptions.title&&wkAxis.userOptions.title.text == units){
				axisFound = true;
				axisIndex = ii;
				break;
			}
		}
		if(!axisFound){
			axisIndex = chart.yAxis.length;
            chart.addAxis(configYAxis(units, min, max,visible));
		}
	}
	return axisIndex;
}

function* addPointData(wkInput, chart, pointDetails, pointData, deselectPointData, coV, fromDate, toDate, now, id){
	if(!chart.options){return;}

	const colors = seriesColor.getSeriesColors();
	let {colorDictionary, colorIndex} = yield select(x => x.seriesColor);

	let color = colorDictionary[pointDetails.name] ? colorDictionary[pointDetails.name] : null;
	if(!color) {
		var newColor = seriesColor.getColorForPoint(pointDetails.name);
		if(newColor) {
			color = newColor;
		} else {
			color = colors[colorIndex];
			colorDictionary[pointDetails.name] = color;
			colorIndex++;
			if(colorIndex >= colors.length) {
				colorIndex = 0;
			}
			yield put(seriesColorActions.newPoint(pointDetails.name, color));
		}
	}

	let newSeries = {
		name: pointName.formatDescription(pointDetails),
		color: color,
		yAxis: getAxisIndex(chart, pointDetails.units),
        data: [],
        marker: {
            enabled: false,
        },
	};
	let isSteppedSeries = steppedSeries.isSteppedSeries(pointDetails.name, pointDetails.description, coV);

	let previousValue = null;
	if(coV && wkInput.source === "IEnergy"){
		previousValue = yield call(pointsservice.TrendableDataGetAsAt, pointDetails.objectID, fromDate);
		if(previousValue instanceof Error || !previousValue){return;}
		if(previousValue && previousValue.length){
			let wkPreviousValue = previousValue[0];
			if(wkPreviousValue){
				newSeries.data.unshift({x: DateTime.getGMTDate(fromDate.format("YYYY-MM-DD HH:mm:ss")).getTime(), y: wkPreviousValue.value, units: pointDetails.units});
			}
		}
	}

	if(pointData && pointData.length){
		pointData.forEach(s => {
			newSeries.data.push({x: DateTime.getGMTDate(s.timestamp).getTime(), y: s.value, units: pointDetails.units});
		});
	}

	if (isSteppedSeries) {
		newSeries.step = 'left';
	}
	
	if(coV){
		//only add a cov up to present time
		const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
		if(pointData && pointData.length){
			newSeries.data.push({x: DateTime.getGMTDate(endDate.format("YYYY-MM-DD HH:mm:ss")).getTime(), y: pointData[pointData.length - 1].value, units: pointDetails.units});
		}else if(previousValue && previousValue.length){
			let wkPreviousValue = previousValue[0];
			if(wkPreviousValue){
				newSeries.data.push({x: DateTime.getGMTDate(endDate.format("YYYY-MM-DD HH:mm:ss")).getTime(), y: wkPreviousValue.value, units: pointDetails.units});
			}
		}
	}
	if(chart && chart.options){
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

function* addOutcomesAsync(chart, ruleInstanceId, fromDate, toDate){
	const outcomePeriods = yield call(reportsservice.GetRunTimesAndFailures, ruleInstanceId, fromDate, toDate, false);
	if(outcomePeriods instanceof Error || !outcomePeriods){return;}
	addOutcomes(chart, outcomePeriods, toDate);
}

//function* addPointDataAsync(wkInput, chart, pointId, fromDate, toDate, coV, index, now, id, deselectPoint){
function* addPointDataAsync(wkInput, chart, pointId, fromDate, toDate, coV, index, now, id, deselectPoint){
	if(wkInput.source === "RuleInstance"){
		const [pointDetails, pointData, deselectPointData] = yield all([
			call(pointsservice.GetRuleInstance, pointId),
			call(pointsservice.GetRuleInstanceData, pointId, wkInput.subValues[0].values[index].value, fromDate, toDate),
			(deselectPoint != undefined && deselectPoint != null) ? 
			call(pointsservice.GetRuleInstanceData, deselectPoint.pointId, wkInput.subValues[0].values[index].value, fromDate, toDate) : undefined
		]);
		yield call(addPointData,wkInput, chart, pointDetails, pointData, deselectPointData, true, fromDate, toDate, now, id);
	}else if (wkInput.source === "IEnergy"){
		const [pointDetails, pointData, deselectPointData] = yield all([
			call(pointsservice.TrendableObjectGetObject, pointId),
			call(pointsservice.TrendableDataGetData, pointId, fromDate, toDate),
			(deselectPoint != undefined && deselectPoint != null) ? 
			call(pointsservice.TrendableDataGetData, deselectPoint.pointId, fromDate, toDate) : undefined
		]);
		yield call(addPointData,wkInput,  chart, pointDetails, pointData, deselectPointData, coV, fromDate, toDate, now, id);
	}
}

function* addUpperLimit(chart, ruleInstanceId, fromDate, toDate, now, id){
	//only add a cov up to present time
	const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
	const result = yield call(reportsservice.GetUpperLimit, ruleInstanceId, fromDate, endDate);
	if(result instanceof Error || !result){return;}
	var yAxisIndex = 0;
	if(result.processVariablePointId){
		const pointDetails = yield call(pointsservice.TrendableObjectGetObject, result.processVariablePointId);
		if(pointDetails instanceof Error || !pointDetails){return;}
		if(pointDetails && pointDetails.units){
			yAxisIndex = getAxisIndex(chart, pointDetails.units);
		}
	}
	let seriesName = "Upper Limit";
	let newSeries = {
        name: seriesName,
        step: 'left',
        yAxis: yAxisIndex,
        color: seriesColor.getColorForPoint(seriesName),
        data: [],
        marker: {
            enabled: false,
        },
	};
	if(result && result.data && result.data.length){
		result.data.forEach(s => {
			newSeries.data.push([new Date(s.timestamp).getTime(), s.value]);
		});
	}
	if(chart && chart.options){
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

function* addLowerLimit(chart, ruleInstanceId, fromDate, toDate, now, id){
	//only add a cov up to present time
	const endDate = now.valueOf() < toDate.valueOf() ? now : toDate;
	const result = yield call(reportsservice.GetLowerLimit, ruleInstanceId, fromDate, endDate);
	if(result instanceof Error || !result){return;}
	var yAxisIndex = 0;
	if(result.processVariablePointId){
		const pointDetails = yield call(pointsservice.TrendableObjectGetObject, result.processVariablePointId);
		if(pointDetails instanceof Error || !pointDetails){return;}
		if(pointDetails && pointDetails.units){
			yAxisIndex = getAxisIndex(chart, pointDetails.units);
		}
	}
	let seriesName = "Lower Limit";
	let newSeries = {
        name: seriesName,
        step: 'left',
        yAxis: yAxisIndex,
        color: seriesColor.getColorForPoint(seriesName),
        data: [],
        marker: {
            enabled: false,
        },
	};
	if(result && result.data && result.data.length){
		result.data.forEach(s => {
			newSeries.data.push([new Date(s.timestamp).getTime(), s.value]);
		});
	}
	if(chart && chart.options){
		chart.addSeries(newSeries);
		yield put(charttooltipactions.addSeries(id, newSeries));
	}
}

//////////
//sagas
export const sagas = {
	*rootSaga() {
		yield all([this.loadData()]);
    },

	*loadData(){
		yield takeEvery(types.LOAD_DATA, function*({payload}){
			const component = payload.component;
			const chart = payload.chart;
			const id = payload.id;
			const fromDate = moment(payload.timePeriod.startDate);
			const toDate = moment(payload.timePeriod.endDate);
			//const report = payload.report;
			let currentSite = yield select(x => x.sitetree.currentSite)
			if (!currentSite) {
				yield take(sitetreetypes.ON_LOAD);
				currentSite = yield select(x => x.sitetree.currentSite);
			}
			let utcOffset = 0;
			if (currentSite.momentTimezone) {
				utcOffset = moment()
					.tz(currentSite.momentTimezone)
					.utcOffset();
			}
			let now = moment( moment()
					.utc()
					.add(utcOffset, "m")
					.format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD HH:mm:ss');

			if(chart.options){
                chart.xAxis[0].setExtremes(moment(fromDate).utc().add(fromDate.utcOffset(), 'm').valueOf(), moment(toDate).utc().add(fromDate.utcOffset(), 'm').valueOf());
			}

			while(chart.series.length > 0) {
				chart.series[0].remove(true);
			}

			//add all calls into aray so can do a yield all on them
			let dataCalls = [];

			for(var ii = 0; ii < component.inputs.length; ii++){
				var wkInput = component.inputs[ii];
				if(wkInput.name === "UpperLimDeviation"){
					dataCalls.push({
						func: addUpperLimit,
						args: [chart, component.ruleInstanceId, fromDate, toDate, now, id]
					});
				}else if(wkInput.name === "LowerLimDeviation"){
					dataCalls.push({
						func: addLowerLimit,
						args: [chart, component.ruleInstanceId, fromDate, toDate, now, id]
					});
				}else{
					for(var jj = 0; jj < wkInput.values.length; jj++){
						var wkValue = wkInput.values[jj];
						//var deselectPoint = getDeselectPoint(report.ruleInstance.config.fieldVars.processVariables, wkValue.value);
						dataCalls.push({
							func: addPointDataAsync,
							//args: [wkInput, chart, wkValue.value, fromDate, toDate, wkValue.coV, jj, now, id, deselectPoint]
							args: [wkInput, chart, wkValue.value, fromDate, toDate, wkValue.coV, jj, now, id]
						});
					}
				}
			}
			dataCalls.push({
				func: addOutcomesAsync,
				args: [chart, component.ruleInstanceId, fromDate, toDate]
			});
			let calls = [];
			dataCalls.forEach(s => calls.push(call(s.func, ...s.args)));
			yield all(calls);

			let list = yield call(rulessservice.GetReportCommentsChart, component.ruleInstanceId, fromDate, toDate);
			let genericComments = yield call(addComments, chart, list, id);
			yield put(actions.setGenericComments(genericComments));

			if(payload.onLoadComplete){
				payload.onLoadComplete();
			}
		});
	},
};
/* // REVM-128
function getDeselectPoint(processVariables, id) {
	for(var ii = 0, ilen = processVariables.length; ii < ilen; ii++) {
		if(processVariables[ii].pointId == id) {
			if(processVariables[ii].deselectPoint && processVariables[ii].deselectPoint != null && processVariables[ii].databaseId != 0) {
				return processVariables[ii].deselectPoint;
			}
		}
	}
	return null;
}
*/

////////
//actions
export const actions = {
	loadData(component, chart, timePeriod, onLoadComplete, id, report){
		return {
			type: types.LOAD_DATA,
			payload: {component, chart, timePeriod, onLoadComplete, id, report}
		};
	},
	setGenericComments(comments) {
		return {
			type: types.SET_GENERIC_COMMENTS,
			payload: { comments }
		};
	}
};



// WEBPACK FOOTER //
// ./src/components/ReportComponents/Charts/ValidationRuleChart/redux.js